import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLoaderData, useNavigate, useLocation } from 'react-router';
import { detailsLoader } from '../../routes';
import Product from '@models/product';
import Coupon from '@models/coupon';
import { authStore, cartStore, wishlistStore } from '@stores/main';
import { observer } from 'mobx-react';
import { useToast } from '../../contexts/ToastContext';
import Loader from '@components/Loader/Loader';
import Header from '../HomeRedesign/components/Header/Header';
import Footer from '../HomeRedesign/components/Footer/Footer';
import PortraitCollection from '../HomeRedesign/components/PortraitCollection/PortraitCollection';
import { generateProductDetailsUrl } from '@utils/urlUtils';
import { formatDiscountPercentage } from '@utils/formatDiscountPercentage';
import { useBreadcrumbNavigation } from '@utils/breadcrumbNavigation';
import type { ProductRedemptionDetails } from '@utils/productRedemptionHtml';
import {
  productHasRedemptionSection,
  optimizeRedemptionInstructions,
  convertTermsToListItem,
} from '@utils/productRedemptionHtml';
import StoresAPI from '@api/stores-api';
import type { ProductGroupItem } from '@api/stores-api';
import { pageTrackingService } from '../../services/pageTracking';
import styles from './ProductDetailsRedesign.module.scss';

const ProductDetailsRedesign: React.FC = observer(() => {
  const {
    info: productInfo,
    isAlreadyPurchased,
    lastPurchasedDate,
    moreProductsForSameGroup,
    showSteamGcAsMain,
    steamGiftCardOffers,
    coupons = [],
  } = useLoaderData<typeof detailsLoader>();
  
  const navigate = useNavigate();
  const { goToLogin } = useBreadcrumbNavigation();
  const location = useLocation();
  const { showToast } = useToast();
  const info = productInfo; // Use product info directly from API
  const redemptionDetails = (info as { redemption_details?: ProductRedemptionDetails })
    .redemption_details;
  const showRedemptionBlock = productHasRedemptionSection(redemptionDetails);
  const storesAPI = useMemo(() => new StoresAPI(), []);
  const productGroupsFetchRef = useRef<{ productId: string; promise: Promise<ProductGroupItem[]> } | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<string>('');
  const [productGroups, setProductGroups] = useState<ProductGroupItem[]>([]);
  const [productGroupsLoading, setProductGroupsLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isInstructionsExpanded, setIsInstructionsExpanded] = useState(false);
  const [isTermsExpanded, setIsTermsExpanded] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isAddingToWishlist, setIsAddingToWishlist] = useState(false);
  const [cartSuccessAnimation, setCartSuccessAnimation] = useState(false);
  const [wishlistSuccessAnimation, setWishlistSuccessAnimation] = useState(false);

  // Product page view with product context (screen_view alone lacks the id).
  // Ref guard keeps StrictMode double-invoke / re-renders to one emit per
  // product.
  const viewedProductIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (info?.id && viewedProductIdRef.current !== info.id) {
      viewedProductIdRef.current = info.id;
      pageTrackingService.trackProductView(info.id, info.title);
    }
  }, [info?.id, info?.title]);

  // Scroll to top on mount and route changes
  useEffect(() => {
    // Scroll to top immediately when component mounts or route changes
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      // Also try scrolling the main container if it exists
      const mainContainer = document.querySelector('[data-scroll-container]');
      if (mainContainer) {
        (mainContainer as HTMLElement).scrollTop = 0;
      }
    };

    // Immediate scroll
    scrollToTop();

    // Also scroll after a small delay to handle any async rendering
    const timeoutId = setTimeout(scrollToTop, 0);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  useEffect(() => {
    // Also scroll to top when product info loads/changes
    if (info?.id) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [info?.id]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  // Fetch product groups for Select Amount (get_product_groups API). Deduplicated so Strict Mode double-mount doesn't call API twice.
  useEffect(() => {
    if (!info?.id) {
      setProductGroups([]);
      setProductGroupsLoading(false);
      productGroupsFetchRef.current = null;
      return;
    }
    const productId = info.id;
    let cancelled = false;

    const doFetch = (): Promise<ProductGroupItem[]> => {
      if (productGroupsFetchRef.current?.productId === productId) {
        return productGroupsFetchRef.current.promise;
      }
      const promise = storesAPI.getProductGroups(productId);
      productGroupsFetchRef.current = { productId, promise };
      return promise;
    };

    setProductGroupsLoading(true);
    doFetch()
      .then((groups) => {
        if (cancelled) return;
        setProductGroups(groups);
        if (groups.length > 0) {
          // Highlight the option that matches the current page product (so after navigation the right one is selected)
          const currentItem = groups.find((g) => g.product_id === info.id) ?? groups.find((g) => g.selected) ?? groups[0];
          const value = currentItem.buy_price_string ?? (currentItem.buy_price != null ? `₹${currentItem.buy_price}` : '');
          if (value) setSelectedAmount(value);
        }
      })
      .catch(() => {
        if (!cancelled) setProductGroups([]);
      })
      .finally(() => {
        if (!cancelled) {
          setProductGroupsLoading(false);
          if (productGroupsFetchRef.current?.productId === productId) {
            productGroupsFetchRef.current = null;
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [info?.id, storesAPI]);

  useEffect(() => {
    // Set default selected amount when no product groups (single product)
    if (productGroupsLoading || productGroups.length > 0) return;
    if (info?.buyPriceString) {
      setSelectedAmount(info.buyPriceString);
    } else if (info?.buyPrice) {
      setSelectedAmount(`₹${info.buyPrice}`);
    }
  }, [info, productGroups.length, productGroupsLoading]);

  useEffect(() => {
    // Prevent horizontal scrolling on mobile
    const originalBodyOverflowX = document.body.style.overflowX;
    const originalHtmlOverflowX = document.documentElement.style.overflowX;
    const originalBodyWidth = document.body.style.width;
    const originalHtmlWidth = document.documentElement.style.width;
    
    // Set overflow and width constraints
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    document.body.style.width = '100%';
    document.documentElement.style.width = '100%';
    document.body.style.maxWidth = '100vw';
    document.documentElement.style.maxWidth = '100vw';
    
    return () => {
      document.body.style.overflowX = originalBodyOverflowX;
      document.documentElement.style.overflowX = originalHtmlOverflowX;
      document.body.style.width = originalBodyWidth;
      document.documentElement.style.width = originalHtmlWidth;
      document.body.style.maxWidth = '';
      document.documentElement.style.maxWidth = '';
    };
  }, []);



  const handleAddToCart = async () => {
    if (!info) return;

    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }

    if (isInCart) {
      // If already in cart, navigate to cart page
      navigate('/cart');
      return;
    }

    setIsAddingToCart(true);
    setCartSuccessAnimation(false);

    try {
      const res = await cartStore.addItemToCart(info.id);
      if (!res) {
        return;
      }
      pageTrackingService.trackProductAddToCart(info.id, info.title, 'product_page');
      setCartSuccessAnimation(true);
      setTimeout(() => {
        setCartSuccessAnimation(false);
      }, 1000);
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    if (!info) return;
    
    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }
    
    setShowLoader(true);
    try {
      const res = await cartStore.addItemToCart(info.id);
      if (!res) {
        setShowLoader(false);
        return;
      }
      pageTrackingService.trackProductBuyNow(info.id, info.title);
      setTimeout(() => {
        navigate('/cart');
      }, 500);
    } catch (error) {
      console.error("Error adding to cart:", error);
      setShowLoader(false);
    }
  };

  if (!info) {
    return (
      <div className={styles.loaderContainer}>
        <Loader size={60} color="#2196F3" />
      </div>
    );
  }

  // Creative fallback when product has no image or image fails to load
  const defaultProductImage = `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;

  // Get product image (use poster if available, otherwise use default)
  const productImage = info.poster || defaultProductImage;

  // Amount options from get_product_groups API, or single option from product info
  const amountOptions = useMemo(() => {
    if (productGroups.length > 0) {
      return productGroups.map((g) => ({
        value: g.buy_price_string ?? (g.buy_price != null ? `₹${g.buy_price}` : ''),
        label: g.label ?? g.buy_price_string ?? '',
        product_id: g.product_id,
      }));
    }
    const value = info.buyPriceString ?? (info.buyPrice != null ? `₹${info.buyPrice}` : '');
    if (!value) return [];
    return [{ value, label: 'Default', product_id: undefined }];
  }, [productGroups, info.buyPriceString, info.buyPrice]);

  const isInCart = cartStore.items.some((item) => item.id === info.id);
  const isInWishlist = wishlistStore.wishlist.some((item) => item.id === info.id);

  // Helper function to convert Product to CollectionProduct format for PortraitCollection
  const convertProductToCollectionFormat = (product: Product | any): any => {
    // Generate product URL only if product has an ID
    let productUrl: string | undefined;
    if (product.id) {
      try {
        productUrl = generateProductDetailsUrl({
          id: product.id,
          title: product.title || product.extendedTitle || 'Product'
        });
      } catch (error) {
        console.warn('Failed to generate product URL:', error);
        productUrl = undefined;
      }
    }

    return {
      id: product.id,
      name: product.title || product.extendedTitle || 'Product',
      price: product.buyPriceString || `₹${product.buyPrice || 0}`,
      originalPrice: product.displayPriceString && product.displayPrice !== product.buyPrice 
        ? product.displayPriceString 
        : undefined,
      discount: product.discountPercentage || undefined,
      gradient: product.background || 'linear-gradient(135deg, #42A5F5, #1E88E5)',
      imageUrl: product.poster || undefined,
      icon: 'fas fa-gamepad',
      tag: product.hotOffer ? 'Hot' : product.buttonDiscountText || undefined,
      link: productUrl,
      productUrl: productUrl
    };
  };

  return (
    <>
      {showLoader && (
        <div className={styles.loaderOverlay}>
          <Loader size={60} color="#2196F3" />
        </div>
      )}
      
      <Header />

      <div className={styles.productDetailsPage}>
        <div className={styles.productContainer}>
          {/* Left Column - Product Image */}
          <div className={styles.productImages}>
            <div className={styles.mainImage}>
              <img 
                src={productImage} 
                alt={info.title}
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  target.onerror = null;
                  target.src = defaultProductImage;
                }}
              />
              {info.isSoldOut && (
                <div className={styles.soldOutBadge}>Out of Stock</div>
              )}
            </div>
          </div>

          {/* Right Column - Product Info */}
          <div className={styles.productInfo}>
            {/* Brand/Store */}
            {info.offerFromStore && (
              <div className={styles.brandName}>{info.offerFromStore.text || 'OnePlay'}</div>
            )}

            {/* Product Title */}
            <div className={styles.productTitleWrapper}>
              <h1 className={styles.productTitle}>{info.title || info.extendedTitle}</h1>
              {/* Special Tag */}
              {(info.hotOffer || info.buttonDiscountText) && (
                <div className={styles.specialTag}>
                  {info.hotOffer ? 'Hot' : info.buttonDiscountText || 'Special'}
                </div>
              )}
            </div>

            {/* Price — hidden when out of stock; tag shown instead */}
            <div className={styles.priceSection}>
              {info.isSoldOut ? (
                <div className={styles.outOfStockPriceTag} role="status">
                  Out of stock
                </div>
              ) : (
                <div className={styles.priceRow}>
                  <div className={styles.priceContainer}>
                    <span className={styles.currentPrice}>
                      {info.buyPriceString || (info.buyPrice != null ? `₹${info.buyPrice}` : 'Price not available')}
                    </span>
                    {info.displayPriceString && info.displayPrice != null && info.buyPrice != null && info.displayPrice !== info.buyPrice && (
                      <span className={styles.originalPrice}>{info.displayPriceString}</span>
                    )}
                  </div>
                  {info.discountPercentage > 0 && (
                    <div className={styles.discountBadge}>{formatDiscountPercentage(info.discountPercentage)}% OFF</div>
                  )}
                </div>
              )}
            </div>

            {/* Select Amount – options from get_product_groups API */}
            {(productGroupsLoading || productGroups.length > 1) && (
              <div className={styles.amountSelector}>
                <h3 className={styles.selectorTitle}>Select Amount</h3>
                {productGroupsLoading ? (
                  <div className={styles.amountOptions}>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className={`${styles.amountOption} ${styles.amountOptionSkeleton}`} aria-hidden />
                    ))}
                  </div>
                ) : (
                  <div className={styles.amountOptions}>
                    {amountOptions.map((option) => (
                      <button
                        key={option.product_id ?? option.value}
                        type="button"
                        className={`${styles.amountOption} ${selectedAmount === option.value ? styles.selected : ''}`}
                        onClick={() => {
                          if (selectedAmount === option.value) return;
                          if (option.product_id && option.product_id !== info.id) {
                            navigate(generateProductDetailsUrl({ id: option.product_id, title: option.label || option.value }));
                            return;
                          }
                          setSelectedAmount(option.value);
                        }}
                      >
                        <div className={styles.amountValue}>{option.value}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              <button
                className={`${styles.addToCartButton} ${cartSuccessAnimation ? styles.addToCartButtonSuccess : ''} ${isInCart ? styles.addToCartButtonInCart : ''}`}
                onClick={handleAddToCart}
                disabled={info.isSoldOut || isAddingToCart}
              >
                {isAddingToCart ? (
                  <>
                    <Loader size={16} color="#FFFFFF" />
                    <span>Adding...</span>
                  </>
                ) : isInCart ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 4L6 11.5L2.5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>View in Cart</span>
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 3H5L5.4 5M7 11H13L15 5H5.4M7 11L5.4 5M7 11L4.7 13.3C4.3 13.7 4.6 14.5 5.1 14.5H13M13 11V13C13 14.1 13.9 15 15 15C16.1 15 17 14.1 17 13V11M6 15.5C6.8 15.5 7.5 16.2 7.5 17C7.5 17.8 6.8 18.5 6 18.5C5.2 18.5 4.5 17.8 4.5 17C4.5 16.2 5.2 15.5 6 15.5ZM17 15.5C17.8 15.5 18.5 16.2 18.5 17C18.5 17.8 17.8 18.5 17 18.5C16.2 18.5 15.5 17.8 15.5 17C15.5 16.2 16.2 15.5 17 15.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Add to Cart</span>
                  </>
                )}
              </button>
              <button
                className={`${styles.wishlistButton} ${isInWishlist ? styles.wishlistButtonActive : ''} ${wishlistSuccessAnimation ? styles.wishlistButtonSuccess : ''}`}
                onClick={async () => {
                  if (!authStore.isAuthenticated) {
                    goToLogin();
                    return;
                  }

                  setIsAddingToWishlist(true);
                  setWishlistSuccessAnimation(false);

                  try {
                    if (isInWishlist) {
                      wishlistStore.removeProductFromWishlist(info as unknown as Product);
                      showToast(`Removed ${info.title} from wishlist`, 'info', 2500);
                    } else {
                      const added = await wishlistStore.addProductToWishlist(info as unknown as Product);
                      if (added) {
                        setWishlistSuccessAnimation(true);
                        setTimeout(() => {
                          setWishlistSuccessAnimation(false);
                        }, 1000);
                      }
                    }
                  } catch (error) {
                    console.error("Error updating wishlist:", error);
                  } finally {
                    setIsAddingToWishlist(false);
                  }
                }}
                disabled={isAddingToWishlist}
                title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                {isAddingToWishlist ? (
                  <Loader size={20} color={isInWishlist ? "#EF4444" : "#2196F3"} />
                ) : (
                  <i className={`fas ${isInWishlist ? 'fa-heart' : 'fa-heart'}`}></i>
                )}
              </button>
            </div>

            {/* Available Coupons - Prominent Section */}
            {coupons.filter(c => c.isApplicable).length > 0 && (
              <div
                id="available-coupons-section"
                className={styles.couponsProminentSection}
                aria-labelledby="available-coupons-heading"
              >
                <div className={styles.couponsProminentHeader}>
                  <div className={styles.couponsProminentHeaderLeft}>
                    <div className={styles.couponsProminentIcon} aria-hidden>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1L10.5 5.5L15.5 6.5L12 10L12.5 15L8 12.5L3.5 15L4 10L0.5 6.5L5.5 5.5L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 id="available-coupons-heading" className={styles.couponsProminentSectionTitle}>
                      Available Coupons
                    </h3>
                  </div>
                </div>
                <div className={styles.couponsProminentList}>
                  {coupons.filter(c => c.isApplicable).map((coupon: Coupon) => (
                    <div 
                      key={coupon.id} 
                      className={`${styles.couponProminentItem} ${coupon.isApplicable ? styles.couponApplicable : styles.couponDisabled}`}
                    >
                      <div className={styles.couponProminentContent}>
                        <div className={styles.couponProminentTextCol}>
                          {coupon.title && (
                            <div className={styles.couponProminentTitle}>{coupon.title}</div>
                          )}
                        </div>
                        <div className={styles.couponCodeWrapper}>
                          <div className={`${styles.couponProminentCode} ${coupon.isApplicable ? styles.couponCodeApplicable : styles.couponCodeDisabled}`}>
                            {coupon.code}
                          </div>
                          {!coupon.isApplicable && (
                            <span className={styles.unavailableBadge}>Unavailable</span>
                          )}
                        </div>
                      </div>
                      <div className={styles.couponProminentHelp}>
                        <button
                          className={`${styles.couponProminentHelpBtn} ${coupon.isApplicable ? '' : styles.couponHelpBtnDisabled}`}
                          disabled={!coupon.isApplicable}
                          title={coupon.isApplicable ? "You can apply coupon code on cart page" : "This coupon is not available"}
                        >
                          <i className="fas fa-question-circle"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            <div className={styles.features}>
              <div className={styles.feature}>
                <div className={styles.featureIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <span>OnePlay Assured</span>
              </div>
              <div className={styles.feature}>
                <div className={styles.featureIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span>Instant delivery</span>
              </div>
              <div className={styles.feature}>
                <div className={styles.featureIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span>Quick Support Available</span>
              </div>
              <div className={styles.feature}>
                <div className={styles.featureIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M8 12L10.5 14.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span>Safe & Money Back Guarantee</span>
              </div>
            </div>

            {/* Payment Security — Razorpay (pill / glass) */}
            <div className={styles.paymentSecurityProminent}>
              <div className={styles.paymentSecurityContent}>
                <div className={styles.paymentSecurityIcon} aria-hidden>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12 3L5 6.5V12C5 16.5 8 20.5 12 22C16 20.5 19 16.5 19 12V6.5L12 3Z"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 12L11 14L15.5 9.5"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className={styles.paymentSecurityTagline}>Safe and secure payments with</p>
                <div className={styles.paymentSecurityLogo}>
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/images/logos/razorpay-logo.png`}
                    alt="Razorpay"
                    className={styles.razorpayLogoProminent}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About and Redemption/Terms Container */}
        <div className={styles.aboutRedemptionContainer}>
          {/* About Section */}
          <div className={styles.aboutSection}>
            <div className={styles.aboutSectionHeader}>
              <div className={styles.aboutSectionIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>About</h2>
            </div>

            <div className={styles.aboutContent}>
              {info.description ? (
                <>
                  <div 
                    className={`${styles.descriptionText} ${!showFullDescription ? styles.truncated : ''}`}
                    dangerouslySetInnerHTML={{ __html: info.description }}
                  />
                  <button 
                    className={`${styles.readMoreButton} ${showFullDescription ? styles.expanded : ''}`}
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    aria-expanded={showFullDescription}
                  >
                    {showFullDescription ? 'Read Less' : 'Read More'}
                  </button>
                </>
              ) : (
                <div className={styles.noDescription}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <p>No description available for this product.</p>
                </div>
              )}
            </div>
          </div>

          {/* Redemption and Terms Container */}
          <div className={styles.redemptionTermsContainer}>
            {/* Product Info Cards - Single Element for All Devices */}
            <div className={styles.productInfoCards}>
              <div className={styles.productInfoGrid}>
                {/* Region Information Row */}
                <div className={styles.productInfoGridRow}>
                  <div className={styles.productInfoGridLabel}>
                    {info.availableRegionLabel?.trim() || "NA"}
                  </div>
                  <div className={styles.productInfoGridValue}>
                    <button 
                      className={styles.productInfoCardButton}
                      type="button"
                      aria-label={`Region: ${info.availableRegionText?.trim() || "NA"}`}
                    >
                      {info.availableRegionText?.trim() || "NA"}
                    </button>
                  </div>
                </div>

                {/* Product Type Row */}
                <div className={styles.productInfoGridRow}>
                  <div className={styles.productInfoGridLabel}>Type of Product</div>
                  <div className={styles.productInfoGridValue}>
                    <button 
                      className={`${styles.productInfoCardButton} ${styles.productInfoCardButtonBlue}`}
                      type="button"
                      aria-label={`Product type: ${info.type || 'Wallet'}`}
                    >
                      {info.type
                        ? info.type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                        : 'Wallet'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Type — only when API provides oplay_store_id (gameStoreId) */}
            {info.offerFromStore &&
              info.gameStoreId != null &&
              String(info.gameStoreId).trim() !== '' && (
              <div className={styles.infoCard}>
                <h3 className={styles.infoCardTitle}>Type of Key</h3>
                <div className={styles.keyTypeContainer}>
                  <div className={styles.keyTypeItem}>
                    <div className={styles.keyTypeIconWrapper}>
                      <div className={styles.keyTypeIcon}>
                        <img 
                          src={info.offerFromStore.icon} 
                          alt={info.offerFromStore.text || 'Store'} 
                          className={styles.keyTypeStoreIcon}
                        />
                      </div>
                      <div className={styles.keyTypeCheckmark}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    <span className={styles.keyTypeText}>{info.offerFromStore.text || 'Verified Store'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* How to Redeem + Terms (API: redemption_details — same behavior as legacy ProductDetails) */}
            {showRedemptionBlock && redemptionDetails && (
              <div className={styles.redemptionDetailsSection}>
                <div className={styles.redemptionHeader}>
                  <div className={styles.redemptionHeaderContent}>
                    <div className={styles.redemptionIcon}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className={styles.redemptionHeaderText}>
                      <div className={styles.redemptionTitleStrip}>
                        <h3 className={styles.redemptionTitle}>How to Redeem</h3>
                        {redemptionDetails.redemption_channels &&
                          Array.isArray(redemptionDetails.redemption_channels) &&
                          redemptionDetails.redemption_channels.length > 0 &&
                          redemptionDetails.redemption_channels.some(
                            (ch) => ch && String(ch).trim() !== ''
                          ) && (
                            <div className={styles.redemptionChannels}>
                              {redemptionDetails.redemption_channels
                                .filter((channel) => channel && String(channel).trim() !== '')
                                .map((channel, index) => (
                                  <span key={index} className={styles.redemptionChannelBadge}>
                                    {String(channel).charAt(0).toUpperCase() + String(channel).slice(1)}
                                  </span>
                                ))}
                            </div>
                          )}
                      </div>
                      <p className={styles.redemptionSubtitle}>View redemption instructions and terms</p>
                    </div>
                  </div>
                </div>

                <div className={styles.redemptionContent}>
                  {redemptionDetails.redemption_instructions &&
                    redemptionDetails.redemption_instructions.trim() !== '' && (
                      <div className={styles.redemptionBlock}>
                        <button
                          type="button"
                          className={styles.redemptionBlockHeader}
                          onClick={() => setIsInstructionsExpanded(!isInstructionsExpanded)}
                          aria-expanded={isInstructionsExpanded}
                          aria-label="Toggle step-by-step instructions"
                        >
                          <div className={styles.redemptionBlockHeaderLeft}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M7.5 10L9 11.5L12.5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <h4 className={styles.redemptionBlockTitle}>Step-by-Step Instructions</h4>
                          </div>
                          <div className={`${styles.redemptionBlockToggle} ${isInstructionsExpanded ? styles.expanded : ''}`}>
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </button>
                        <div
                          className={`${styles.redemptionInstructionsContent} ${isInstructionsExpanded ? styles.expanded : ''}`}
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: optimizeRedemptionInstructions(redemptionDetails.redemption_instructions),
                            }}
                          />
                          {redemptionDetails.redemption_url &&
                            redemptionDetails.redemption_url.trim() !== '' && (
                              <div className={styles.redemptionUrlLink}>
                                <a
                                  href={redemptionDetails.redemption_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.redemptionUrlLinkText}
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 3.5H3C2.44772 3.5 2 3.94772 2 4.5V12.5C2 13.0523 2.44772 13.5 3 13.5H11C11.5523 13.5 12 13.0523 12 12.5V9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M9 2.5H14V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M7 8.5L14 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  <span>Redeem here</span>
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.5 10.5L10.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M3.5 3.5H10.5V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </a>
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                  {redemptionDetails.terms && redemptionDetails.terms.trim() !== '' && (
                    <div className={styles.redemptionBlock}>
                      <button
                        type="button"
                        className={styles.redemptionBlockHeader}
                        onClick={() => setIsTermsExpanded(!isTermsExpanded)}
                        aria-expanded={isTermsExpanded}
                        aria-label="Toggle terms and conditions"
                      >
                        <div className={styles.redemptionBlockHeaderLeft}>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M10 6.5V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M10 13H10.0075" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <h4 className={styles.redemptionBlockTitle}>Terms and Conditions</h4>
                        </div>
                        <div className={`${styles.redemptionBlockToggle} ${isTermsExpanded ? styles.expanded : ''}`}>
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </button>
                      <div
                        className={`${styles.redemptionTermsContent} ${isTermsExpanded ? styles.expanded : ''}`}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: convertTermsToListItem(redemptionDetails.terms),
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Product Info Section */}
        <div className={styles.additionalInfoSection}>
          <div className={styles.additionalInfoGrid}>
            {/* Developer Section */}
            {info.developers && info.developers.length > 0 && (
              <div className={styles.infoCard}>
                <h3 className={styles.infoCardTitle}>Developer</h3>
                <div className={styles.developerList}>
                  {info.developers.map((developer: string, index: number) => (
                    <div key={index} className={styles.developerItem}>{developer}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Similar Products Section */}
        {info.similarProducts &&
         info.similarProducts.items &&
         info.similarProducts.items.length > 0 && (
          <PortraitCollection
            title={info.similarProducts.title || 'Similar Products'}
            subtitle="Products you might like"
            products={info.similarProducts.items.map((item: any) => 
              convertProductToCollectionFormat(new Product(item))
            )}
          />
        )}

        {/* More Products from Same Group */}
        {moreProductsForSameGroup && moreProductsForSameGroup.length > 0 && (
          <PortraitCollection
            title="More Products"
            subtitle="From the same collection"
            products={moreProductsForSameGroup.map((p: any) => 
              convertProductToCollectionFormat(new Product(p))
            )}
          />
        )}
        
      </div>
      
      <Footer />
    </>
  );
});

export default ProductDetailsRedesign;

